/**
 * Difficulty ramp — a pure function of how long the run has survived. Driving
 * difficulty by elapsed time (not the displayed score) keeps it independent of the
 * score scale and identical between client and server.
 *
 * Owner-spec ramp: the attack SPEED steps up at 20s / 30s / 40s / 60s / 120s, then
 * every {@link SPEED_REPEAT_MS} (2 min) after that. Each step multiplies the attack
 * rate by {@link SPEED_MULTIPLIER} (≈1.28×) — i.e. divides the attack/spacing timings
 * — floored so the fastest tiers stay playable and capped at {@link MAX_SPEED_STEP}.
 */
import {
  BASE_ATTACK_SPEED_MS,
  BASE_SPACING_MS,
  MAX_SPEED_STEP,
  MIN_ATTACK_SPEED_MS,
  MIN_SPACING_MS,
  SPEED_MULTIPLIER,
  SPEED_REPEAT_MS,
  SPEED_THRESHOLDS_MS,
  type DifficultyTier,
} from './constants';

/**
 * How many speed-ups have happened by `elapsedMs` (0 before the first). Increments
 * at each {@link SPEED_THRESHOLDS_MS} mark, then every {@link SPEED_REPEAT_MS} after
 * the last mark, clamped to {@link MAX_SPEED_STEP}. The renderer watches this to flash
 * "SPEED INCREASED!" when it changes.
 */
export function speedStepForElapsed(elapsedMs: number): number {
  let step = 0;
  for (const t of SPEED_THRESHOLDS_MS) {
    if (elapsedMs >= t) step++;
  }
  const last = SPEED_THRESHOLDS_MS[SPEED_THRESHOLDS_MS.length - 1];
  if (elapsedMs > last) {
    step += Math.floor((elapsedMs - last) / SPEED_REPEAT_MS);
  }
  return Math.min(MAX_SPEED_STEP, step);
}

/** The active difficulty for a given elapsed survival time (ms). */
export function difficultyForElapsed(elapsedMs: number): DifficultyTier {
  const step = speedStepForElapsed(elapsedMs);
  const mult = Math.pow(SPEED_MULTIPLIER, step);
  return {
    // Level is the 1-based step (drives pattern weighting + turret count, capped there).
    level: step + 1,
    attackSpeedMs: Math.max(MIN_ATTACK_SPEED_MS, Math.round(BASE_ATTACK_SPEED_MS / mult)),
    spacingMs: Math.max(MIN_SPACING_MS, Math.round(BASE_SPACING_MS / mult)),
  };
}
